import { useState } from "react";

import { format, parse } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { Calendar } from "@components/Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@components/Popover";

import { cn } from "@lib/utils";

const DEFAULT_FORMAT = "yyyy-MM-dd";

/** Converts moment-style format to date-fns format for display. */
const toDateFnsFormat = (formatStr: string): string =>
	formatStr.replace(/YYYY/g, "yyyy").replace(/DD/g, "dd").replace(/Y/g, "y").replace(/D/g, "d");

const parseValue = (value: Date | string | null | undefined, formatStr: string): Date | undefined => {
	if (value === null) return undefined;
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
	try {
		const d = parse(String(value), formatStr, new Date());
		return Number.isNaN(d.getTime()) ? undefined : d;
	} catch {
		return undefined;
	}
};

const calendarDropdownBounds = {
	captionLayout: "dropdown" as const,
	startMonth: new Date(1900, 0),
	endMonth: new Date(),
};

const datePickerTriggerBaseClass =
	"flex items-center justify-start gap-2 text-left text-white font-normal border border-secondary rounded-sm hover:border-white bg-transparent px-2.5 h-8 text-sm w-full outline-none transition-colors disabled:opacity-50";

export interface DatePickerProps {
	value?: Date | string | null;
	onChange?: (date: Date | undefined, dateString: string) => void;
	disabledDate?: (date: Date) => boolean;
	format?: string;
	placeholder?: string;
	className?: string;
	triggerClassName?: string;
	status?: "error" | "";
}

/** A date picker component with range and presets.
 * @prop value - Controlled selected date (Date or string in format)
 * @prop onChange - Called when the user selects a date or clears. (date, dateString) for compatibility with string-based state.
 * @prop disabledDate - Disable specific dates. Receives a Date; return true to disable.
 * @prop format - Display format (e.g. "yyyy-MM-dd" or "YYYY-MM-DD"). Stored dateString uses the same pattern.
 * @prop placeholder - Placeholder text for the date picker.
 * @prop className - Class name for the date picker container.
 * @prop triggerClassName -  Trigger button class (e.g. for width).
 * @prop status - "error" for invalid/error state styling.
 */
const DatePicker = ({
	value,
	onChange,
	disabledDate,
	format: formatProp = DEFAULT_FORMAT,
	placeholder = "Pick a date",
	className,
	triggerClassName,
	status = "",
}: DatePickerProps) => {
	const [open, setOpen] = useState(false);
	const formatStr = toDateFnsFormat(formatProp);
	const dateValue = parseValue(value, formatStr);

	const handleSelect = (date: Date | undefined) => {
		if (date) {
			onChange?.(date, format(date, formatStr));
		} else {
			onChange?.(undefined, "");
		}
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				render={
					<button
						type="button"
						data-empty={!dateValue}
						data-status={status || undefined}
						className={cn(
							"data-[empty=true]:text-muted-foreground",
							datePickerTriggerBaseClass,
							status === "error" && "border-destructive aria-invalid:ring-destructive/20",
							triggerClassName,
						)}
					/>
				}
			>
				<CalendarIcon className="text-muted-foreground size-4 shrink-0" />
				{dateValue ? format(dateValue, formatStr) : <span>{placeholder}</span>}
			</PopoverTrigger>
			<PopoverContent className={cn("w-auto p-0", className)} align="start">
				<Calendar
					key={dateValue?.getTime() ?? "empty"}
					mode="single"
					selected={dateValue}
					onSelect={handleSelect}
					disabled={disabledDate}
					{...calendarDropdownBounds}
					defaultMonth={dateValue ?? undefined}
				/>
			</PopoverContent>
		</Popover>
	);
};

export interface DateRangePickerProps {
	value?: DateRange | undefined;
	onChange?: (range: { from: Date; to: Date }) => void;
	format?: string;
	startPlaceholder?: string;
	endPlaceholder?: string;
	className?: string;
	triggerClassName?: string;
}

/**
 * Range picker: Default is two-month calendar (mode="range"). Opening clears in-progress selection;
 * popover closes after start + end are chosen (see v9 same-day handling in handleRangeSelect).
 */
const DateRangePicker = ({
	value,
	onChange,
	format: formatProp = DEFAULT_FORMAT,
	startPlaceholder = "Start",
	endPlaceholder = "End",
	className,
	triggerClassName,
}: DateRangePickerProps) => {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState<DateRange | undefined>(undefined);
	const [calendarKey, setCalendarKey] = useState(0);
	const formatStr = toDateFnsFormat(formatProp);

	const handleOpenChange = (next: boolean) => {
		setOpen(next);
		if (next) {
			setDraft(undefined);
			setCalendarKey((k) => k + 1);
		}
	};

	const handleRangeSelect = (range: DateRange | undefined) => {
		setDraft(range);
		if (range?.from && range?.to) {
			onChange?.({ from: range.from, to: range.to });
			setOpen(false);
		}
	};

	const startLabel = open
		? draft?.from
			? format(draft.from, formatStr)
			: undefined
		: value?.from
			? format(value.from, formatStr)
			: undefined;
	const endLabel = open
		? draft?.to
			? format(draft.to, formatStr)
			: undefined
		: value?.to
			? format(value.to, formatStr)
			: undefined;

	const defaultMonth = draft?.from ?? value?.from ?? new Date();

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger
				render={
					<button
						type="button"
						className={cn(datePickerTriggerBaseClass, triggerClassName)}
						aria-label="Select date range"
					/>
				}
			>
				<CalendarIcon className="text-muted-foreground size-4 shrink-0" />
				<div className="flex min-w-0 flex-1 items-center gap-2">
					<span className={cn("min-w-0 flex-1 truncate text-left", !startLabel ? "text-muted-foreground" : undefined)}>
						{startLabel ?? startPlaceholder}
					</span>
					<span className={cn("min-w-0 flex-1 truncate text-left", !endLabel ? "text-muted-foreground" : undefined)}>
						{endLabel ?? endPlaceholder}
					</span>
				</div>
			</PopoverTrigger>
			<PopoverContent className={cn("w-auto max-w-none p-0", className)} align="start">
				<Calendar
					key={calendarKey}
					mode="range"
					min={1}
					resetOnSelect
					numberOfMonths={2}
					selected={draft}
					onSelect={handleRangeSelect}
					{...calendarDropdownBounds}
					defaultMonth={defaultMonth}
				/>
			</PopoverContent>
		</Popover>
	);
};

DatePicker.displayName = "DatePicker";
DateRangePicker.displayName = "DateRangePicker";

export { DatePicker, DateRangePicker };
